import {
  addMonths,
  addYears,
  differenceInCalendarDays,
  format,
  isValid,
  parseISO,
} from "date-fns";

import { parseNumericValue, truncateDecimals } from "./numberFormatter";
import {
  buildZar8YieldPerformanceSeries,
  getZar8YieldPerformanceDelta,
} from "./zar8YieldOverview";
import { MOCK_ZAR8_YIELD_PERFORMANCE_GRAPH } from "./zar8YieldOverviewMocks";

const DEFAULT_ASSET_SYMBOL = "ZAR8";
const DEFAULT_YIELD_ASSET_SYMBOL = "BTC";

const normalizeDateValue = (value) => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return isValid(value) ? value : null;
  }

  if (typeof value === "number") {
    const dateValue = new Date(value);
    return isValid(dateValue) ? dateValue : null;
  }

  if (typeof value === "string") {
    const parsedDate = parseISO(value);

    if (isValid(parsedDate)) {
      return parsedDate;
    }

    const fallbackDate = new Date(value);
    return isValid(fallbackDate) ? fallbackDate : null;
  }

  return null;
};

const getDurationParts = (entry) => {
  const period =
    entry?.productId?.duration?.period ??
    entry?.productId?.durationInYearOrMonth?.[0] ??
    entry?.duration?.period;
  const unit =
    entry?.productId?.duration?.unit ??
    entry?.productId?.durationInYearOrMonth?.[1] ??
    entry?.duration?.unit;
  const normalizedPeriod = Number.parseInt(`${period || ""}`, 10);

  return {
    period: Number.isFinite(normalizedPeriod) ? normalizedPeriod : 0,
    unit: typeof unit === "string" ? unit.trim().toLowerCase() : "",
  };
};

const getPositionStartDate = (entry, snapshot) =>
  normalizeDateValue(
    entry?.startDate ??
      snapshot?.startDate ??
      entry?.createdAt ??
      entry?.productId?.startDate ??
      entry?.productId?.createdAt
  );

const getPositionMaturityDate = (entry, snapshot, startDate) => {
  const explicitDate = normalizeDateValue(
    entry?.maturityDate ??
      snapshot?.maturityDate ??
      entry?.endDate ??
      entry?.productId?.maturityDate ??
      entry?.productId?.endDate
  );

  if (explicitDate) {
    return explicitDate;
  }

  if (!startDate) {
    return null;
  }

  const { period, unit } = getDurationParts(entry || snapshot);

  if (period <= 0 || !unit) {
    return null;
  }

  if (unit.startsWith("month")) {
    return addMonths(startDate, period);
  }

  if (unit.startsWith("year")) {
    return addYears(startDate, period);
  }

  return null;
};

const getTitle = (entry, snapshot) =>
  (
    entry?.productId?.name ||
    snapshot?.title ||
    snapshot?.label ||
    entry?.name ||
    "Yield Position"
  ).toUpperCase();

const getLockedValue = (entry, snapshot) =>
  parseNumericValue(
    entry?.capital ??
      entry?.valueInZar ??
      entry?.valueInLocalCurrency ??
      snapshot?.lockedAmount ??
      entry?.productId?.valueInLocalCurrency ??
      entry?.productId?.valueInZar ??
      entry?.productId?.value ??
      entry?.amount
  );

const getCurrentValueFallback = (entry, snapshot, lockedValue) => {
  const explicitValue = parseNumericValue(
    entry?.balance ??
      entry?.currentValue ??
      entry?.valueInZar ??
      entry?.valueInLocalCurrency ??
      snapshot?.currentValue
  );

  if (explicitValue > 0) {
    return explicitValue;
  }

  const profitValue = parseNumericValue(entry?.profit ?? snapshot?.profit);

  if (lockedValue > 0 || profitValue !== 0) {
    return Math.max(lockedValue + profitValue, 0);
  }

  return lockedValue;
};

const getMarketAsset = (marketAssets, symbol) => {
  if (!marketAssets) {
    return null;
  }

  const normalizedSymbol = `${symbol || ""}`.toUpperCase();
  const matchedEntry = Object.entries(marketAssets).find(
    ([key, data]) =>
      key?.toUpperCase() === normalizedSymbol ||
      data?.symbol?.toUpperCase() === normalizedSymbol
  );

  return matchedEntry ? matchedEntry[1] : null;
};

const getAssetPriceInZar = (marketAssets, symbol) => {
  const marketAsset = getMarketAsset(marketAssets, symbol);

  return parseNumericValue(
    marketAsset?.priceInZar ?? marketAsset?.info?.zarprice ?? marketAsset?.price
  );
};

const getBtcEquivalent = (valueZar, marketAssets) => {
  const btcPriceInZar = getAssetPriceInZar(
    marketAssets,
    DEFAULT_YIELD_ASSET_SYMBOL
  );

  if (btcPriceInZar <= 0) {
    return 0;
  }

  return valueZar / btcPriceInZar;
};

const normalizeHistoryStatus = (status) => {
  const normalizedStatus = `${status || "success"}`.trim().toLowerCase();

  if (normalizedStatus === "successful" || normalizedStatus === "success") {
    return "SUCCESS";
  }

  if (normalizedStatus === "pending") {
    return "PENDING";
  }

  if (normalizedStatus === "failed" || normalizedStatus === "error") {
    return "FAILED";
  }

  return normalizedStatus.toUpperCase();
};

const mapHistoryType = (type) => {
  const normalizedType = `${type || ""}`.trim().toLowerCase();

  if (normalizedType === "deposit" || normalizedType === "locked") {
    return "locked";
  }

  if (normalizedType === "withdrawal" || normalizedType === "unlocked") {
    return "unlocked";
  }

  if (normalizedType === "payout" || normalizedType === "yield") {
    return "yield";
  }

  return "activity";
};

const formatHistoryLabel = (historyType) => {
  if (historyType === "locked") {
    return "Locked";
  }

  if (historyType === "unlocked") {
    return "Unlocked";
  }

  if (historyType === "yield") {
    return "Yield payout";
  }

  return "Activity";
};

export const buildMockZar8YieldHistory = () => [
  {
    id: "mock-yield-history-1",
    type: "locked",
    label: "Locked",
    createdAt: "2025-05-12T00:00:00.000Z",
    amountZar: 200,
    amountBtc: 0.00123,
    status: "SUCCESS",
  },
  {
    id: "mock-yield-history-2",
    type: "unlocked",
    label: "Unlocked",
    createdAt: "2025-05-18T00:00:00.000Z",
    amountZar: 200,
    amountBtc: 0.00123,
    status: "SUCCESS",
  },
  {
    id: "mock-yield-history-3",
    type: "yield",
    label: "Yield payout",
    createdAt: "2025-05-25T00:00:00.000Z",
    amountZar: 200,
    amountBtc: 0.00123,
    status: "SUCCESS",
  },
  {
    id: "mock-yield-history-4",
    type: "locked",
    label: "Locked",
    createdAt: "2025-06-01T00:00:00.000Z",
    amountZar: 200,
    amountBtc: 0.00123,
    status: "SUCCESS",
  },
  {
    id: "mock-yield-history-5",
    type: "unlocked",
    label: "Unlocked",
    createdAt: "2025-06-08T00:00:00.000Z",
    amountZar: 200,
    amountBtc: 0.00123,
    status: "SUCCESS",
  },
];

export const buildZar8YieldPositionHistory = ({
  history = [],
  marketAssets = null,
  isMock = false,
}) => {
  if (isMock) {
    return buildMockZar8YieldHistory();
  }

  return (Array.isArray(history) ? history : [])
    .map((item, index) => {
      const amountZar = parseNumericValue(
        item?.valueInZar ??
          item?.valueInLocalCurrency ??
          item?.zarAmount ??
          item?.amount ??
          item?.transactionRef?.amount ??
          item?.transactionRef?.totalAmount ??
          item?.transactionRef?.total
      );
      const type = mapHistoryType(
        item?.type ?? item?.transactionType ?? item?.subtype
      );

      return {
        id: item?._id || item?.id || `${type}-${index}`,
        type,
        label: formatHistoryLabel(type),
        createdAt: item?.createdAt || item?.date || null,
        amountZar,
        amountBtc:
          parseNumericValue(item?.cryptoEstimate ?? item?.amountBtc) ||
          getBtcEquivalent(amountZar, marketAssets),
        status: normalizeHistoryStatus(item?.status),
      };
    })
    .sort((firstItem, secondItem) => {
      const firstDate = normalizeDateValue(firstItem.createdAt)?.getTime() || 0;
      const secondDate =
        normalizeDateValue(secondItem.createdAt)?.getTime() || 0;

      return secondDate - firstDate;
    });
};

export const getZar8YieldPositionProgress = ({
  startDate,
  maturityDate,
  now = new Date(),
}) => {
  const normalizedStartDate = normalizeDateValue(startDate);
  const normalizedMaturityDate = normalizeDateValue(maturityDate);
  const normalizedNow = normalizeDateValue(now) || new Date();

  if (!normalizedStartDate || !normalizedMaturityDate) {
    return {
      completedPercentage: 0,
      remainingPercentage: 100,
      isMatured: false,
    };
  }

  const totalDays = differenceInCalendarDays(
    normalizedMaturityDate,
    normalizedStartDate
  );

  if (totalDays <= 0) {
    return {
      completedPercentage: 100,
      remainingPercentage: 0,
      isMatured: true,
    };
  }

  const elapsedDays = differenceInCalendarDays(
    normalizedNow,
    normalizedStartDate
  );
  const clampedProgress = Math.min(Math.max(elapsedDays / totalDays, 0), 1);
  const completedPercentage = Math.round(clampedProgress * 100);

  return {
    completedPercentage,
    remainingPercentage: Math.max(100 - completedPercentage, 0),
    isMatured: clampedProgress >= 1,
  };
};

export const buildZar8YieldPositionSeries = ({
  graphData,
  isMock = false,
  useMockFallback = false,
}) => {
  const liveSeries = buildZar8YieldPerformanceSeries(graphData);

  if (isMock || (useMockFallback && liveSeries.length === 0)) {
    return buildZar8YieldPerformanceSeries(MOCK_ZAR8_YIELD_PERFORMANCE_GRAPH);
  }

  return liveSeries;
};

export const buildZar8YieldPositionDetails = ({
  ledgerEntry = null,
  marketAssets = null,
  performanceSeries = [],
  positionSnapshot = null,
}) => {
  const title = getTitle(ledgerEntry, positionSnapshot);
  const assetSymbol =
    ledgerEntry?.productId?.assetId?.asset?.toUpperCase() ||
    positionSnapshot?.assetSymbol ||
    DEFAULT_ASSET_SYMBOL;
  const yieldAssetSymbol = DEFAULT_YIELD_ASSET_SYMBOL;
  const lockedValue = getLockedValue(ledgerEntry, positionSnapshot);
  const performanceDelta =
    performanceSeries.length > 1
      ? getZar8YieldPerformanceDelta(performanceSeries)
      : {
          deltaValue: parseNumericValue(
            ledgerEntry?.profit ?? positionSnapshot?.profit
          ),
          deltaPercentage:
            lockedValue > 0
              ? (parseNumericValue(
                  ledgerEntry?.profit ?? positionSnapshot?.profit
                ) /
                  lockedValue) *
                100
              : 0,
        };
  const currentValue =
    performanceSeries.at(-1)?.value ??
    getCurrentValueFallback(ledgerEntry, positionSnapshot, lockedValue);
  const startDate = getPositionStartDate(ledgerEntry, positionSnapshot);
  const maturityDate = getPositionMaturityDate(
    ledgerEntry,
    positionSnapshot,
    startDate
  );
  const progress = getZar8YieldPositionProgress({
    startDate,
    maturityDate,
  });

  return {
    assetSymbol,
    currentBtcEquivalent: getBtcEquivalent(currentValue, marketAssets),
    currentValue,
    deltaPercentage: performanceDelta.deltaPercentage,
    deltaValue: performanceDelta.deltaValue,
    isMatured: progress.isMatured,
    lockedValue,
    maturityDate,
    progressCompleted: progress.completedPercentage,
    progressRemaining: progress.remainingPercentage,
    startDate,
    title,
    yieldAssetSymbol,
  };
};

const buildPositionEventPayload = (payload = {}) => ({
  product: "zar8_yield",
  ...payload,
});

export const emitZar8YieldPositionEvent = (eventName, payload = {}) => {
  if (typeof window === "undefined") {
    return;
  }

  const eventPayload = buildPositionEventPayload(payload);

  if (window.analytics?.track) {
    window.analytics.track(eventName, eventPayload);
  }

  if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push({
      event: eventName,
      ...eventPayload,
    });
  }

  window.dispatchEvent(
    new CustomEvent("zar8-yield-position", {
      detail: {
        eventName,
        payload: eventPayload,
      },
    })
  );
};

export const formatZar8YieldPositionDate = (value) => {
  const normalizedDate = normalizeDateValue(value);

  if (!normalizedDate) {
    return "TBD";
  }

  return format(normalizedDate, "dd MMM yyyy");
};

export const formatZar8YieldBtcValue = (
  value,
  assetSymbol = DEFAULT_YIELD_ASSET_SYMBOL
) => `${assetSymbol} ${truncateDecimals(Math.abs(value || 0), 4)}`;
